import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { distinctUntilChanged, switchMap } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { GROWTHBOOK } from '../../../growthbook';
import { DialogService } from '../../../services/dialog.service';
import { ShareService } from '../../../services/share.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { EmbedGalleryDialogComponent } from '../../dialogs/embed-gallery-dialog/embed-gallery-dialog.component';
import { ShareGalleryAppDialogComponent } from '../../dialogs/share-gallery-app-dialog/share-gallery-app-dialog.component';
import { ShareGalleryQrCodeDialogComponent } from '../../dialogs/share-gallery-qr-code-dialog/share-gallery-qr-code-dialog.component';

@Component({
  selector: 'app-share-gallery-dialog-footer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, SvgComponent, ShareDirective, RouterLink, ContextMenuComponent, ButtonMenuItemComponent],
  templateUrl: './share-gallery-dialog-footer.component.html',
  styleUrl: './share-gallery-dialog-footer.component.scss',
})
export class ShareGalleryDialogFooterComponent {
  public growthBook = inject(GROWTHBOOK);
  private dialog = inject(DialogService);
  private toasts = inject(ToastService);
  private state = inject(StateService);
  private shareService = inject(ShareService);
  gallery = input.required<GalleryDto>();

  canShare = computed(() => navigator.share && ScreenSizes.screenSize() !== 'desktop');

  shareLink = toSignal(
    toObservable(this.gallery).pipe(
      distinctUntilChanged((a, b) => a?.path === b?.path),
      switchMap(gallery => this.shareService.getShareLink(gallery, { forceSubdomain: true }))
    )
  );

  emailHref = computed(() => {
    const gallery = this.gallery();
    const shareLink = this.shareLink()?.url;
    if (!gallery || !shareLink) return;
    return `mailto:?subject=${encodeURIComponent(gallery.title)}&body=${encodeURIComponent(this.getEmailText(gallery, shareLink))}`;
  });

  private socialText(gallery: GalleryDto, shareLink: string) {
    if (gallery.password_enabled) {
      return `Hello, Here's the link to the gallery: ${shareLink}. The password is: ${gallery.password}`;
    } else {
      return `Hello, Here's the link to the gallery: ${shareLink}`;
    }
  }

  private getEmailText(gallery: GalleryDto, shareLink: string) {
    if (gallery?.password_enabled) {
      return `Hello,\n\nHere's the link to the gallery: ${shareLink}\n\nThe password is: ${
        gallery.password
      }\n\nBest regards,\n${this.state.user?.first_name || ''}\n`;
    } else {
      return `Hello,\n\nHere's the link to the gallery: ${shareLink}\n\nBest regards,\n${
        this.state.user?.first_name || ''
      }\n`;
    }
  }

  whatsappHref = computed(() => {
    const shareLink = this.shareLink();
    const gallery = this.gallery();
    if (!shareLink || !gallery) return;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(this.socialText(gallery, shareLink.url))}`;
  });

  facebookHref = computed(() => {
    const shareLink = this.shareLink();
    if (!shareLink) return;
    return `https://www.facebook.com/dialog/feed?app_id=861804031074445&display=popup&link=${encodeURIComponent(shareLink.url)}`;
  });

  twitterHref = computed(() => {
    const shareLink = this.shareLink();
    const gallery = this.gallery();
    if (!shareLink || !gallery) return;
    return `https://twitter.com/intent/tweet?url=${shareLink.url}&text=${this.socialText(gallery, shareLink.url)}`;
  });

  linkedinHref = computed(() => {
    const shareLink = this.shareLink();
    if (!shareLink) return;
    return `https://www.linkedin.com/shareArticle?mini=true&url=${shareLink.url}`;
  });

  openEmbedDialog() {
    this.dialog.open(EmbedGalleryDialogComponent, { gallery: this.gallery() });
  }

  openAppDialog() {
    this.dialog.open(ShareGalleryAppDialogComponent, { gallery: this.gallery() });
  }

  openQrCodeDialog() {
    this.dialog.open(ShareGalleryQrCodeDialogComponent, { gallery: this.gallery() });
  }

  onLinkCopied() {
    this.toasts.add('Link copied', 'success');
  }
}
